import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Avatar as AvatarT } from '@/api/types';
import { Logo } from '@/components/ui/Logo';
import { Avatar } from '@/components/ui/Avatar';

/** The blobs stuck on the red field (the same pair as the landing page, plus one). */
const PAIR: AvatarT[] = [
  { hue: 220, shape: 'flower', face: 'grin' },
  { hue: 50, shape: 'blob', face: 'wink' },
  { hue: 130, shape: 'star', face: 'smile' },
];

/**
 * Sign-in frame (docs/DESIGN.md): the form on bone, the poster field beside
 * it. On phones the field is a red band on top with two blobs stuck over
 * its edge; on desktop it is the right half, with the blobs at full size.
 */
export function AuthLayout({ title, sub, children, footer }: { title: ReactNode; sub?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-[100dvh] bg-ground lg:grid-cols-[1fr_1fr]">
      <div className="flex min-w-0 flex-col">
        <div className="relative bg-red text-night lg:bg-transparent lg:text-fg">
          <div className="px-4 pb-16 pt-4 sm:px-10 lg:pb-0 lg:pt-6">
            <Link to="/" aria-label="Popin home" className="inline-flex rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg">
              <Logo />
            </Link>
          </div>
          <div className="absolute bottom-0 right-4 flex translate-y-1/2 items-end sm:right-10 lg:hidden" aria-hidden>
            <span className="-rotate-[5deg]">
              <Avatar avatar={PAIR[0]} size={92} sticker />
            </span>
            <span className="-ml-6 mb-1 rotate-[6deg]">
              <Avatar avatar={PAIR[1]} size={72} sticker />
            </span>
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-[460px] flex-1 flex-col justify-center px-4 pb-10 pt-14 sm:px-0 lg:py-10">
          <h1 className="font-display text-[44px] leading-[0.9] sm:text-[52px]">{title}</h1>
          {sub && <p className="mt-3 text-[17px] text-muted">{sub}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-[13px] text-muted">{footer}</div>}
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-red text-night lg:block" aria-hidden>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative flex items-end">
            <span className="relative z-[1] -rotate-[5deg]">
              <Avatar avatar={PAIR[0]} size={260} sticker />
            </span>
            <span className="-ml-16 mb-6 rotate-[6deg]">
              <Avatar avatar={PAIR[1]} size={200} sticker />
            </span>
            <span className="absolute -top-28 left-24 rotate-[4deg]">
              <Avatar avatar={PAIR[2]} size={132} sticker />
            </span>
          </div>
        </div>
        <p className="absolute bottom-12 left-12 text-[22px] font-semibold">Real people. Right now.</p>
      </div>
    </div>
  );
}
